import React, { useMemo, useRef, useState } from "react";
import { BarChart3, Calendar, ChevronDown, ChevronUp, Clock, FileImage, FileText, Loader2, Maximize2, Phone, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PanelModal, PanelModalContent, PanelModalHeaderActions } from "@/components/ui/panel-modal";
import { formatTalkTime } from "@/utils/helpers";
import {
  buildBreakOverviewFileName,
  exportBreakOverviewPdf,
  exportBreakOverviewPng,
} from "@/utils/exportBreakOverviewCapture";
import BreakShiftTimelineChart from "@/components/reports/BreakShiftTimelineChart";

const DAY_PALETTE = [
  { bar: "bg-blue-500", text: "text-blue-500", soft: "bg-blue-500/15 border-blue-500/25", header: "from-blue-500/20" },
  { bar: "bg-emerald-500", text: "text-emerald-500", soft: "bg-emerald-500/15 border-emerald-500/25", header: "from-emerald-500/20" },
  { bar: "bg-amber-500", text: "text-amber-500", soft: "bg-amber-500/15 border-amber-500/25", header: "from-amber-500/20" },
  { bar: "bg-violet-500", text: "text-violet-500", soft: "bg-violet-500/15 border-violet-500/25", header: "from-violet-500/20" },
  { bar: "bg-rose-500", text: "text-rose-500", soft: "bg-rose-500/15 border-rose-500/25", header: "from-rose-500/20" },
  { bar: "bg-cyan-500", text: "text-cyan-500", soft: "bg-cyan-500/15 border-cyan-500/25", header: "from-cyan-500/20" },
  { bar: "bg-orange-500", text: "text-orange-500", soft: "bg-orange-500/15 border-orange-500/25", header: "from-orange-500/20" },
  { bar: "bg-fuchsia-500", text: "text-fuchsia-500", soft: "bg-fuchsia-500/15 border-fuchsia-500/25", header: "from-fuchsia-500/20" },
];

const formatReportDate = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const sortDaysChronological = (days) =>
  [...(days || [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));

const getDayTotalTalk = (day) =>
  (Number(day.total_talk_seconds) || 0) + (Number(day.off_shift_talk_seconds) || 0);

const MetricBar = ({ value, max, barClass, isDark }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={`break-analysis-metric-bar h-2.5 rounded-full overflow-hidden ${isDark ? "bg-slate-700/60" : "bg-gray-200"}`}>
      <div className={`h-full rounded-full transition-all ${barClass}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const MetricValueCell = ({ display, isText, theme, isDark, hasActivity, showBar, bar }) => {
  if (!hasActivity && !isText) {
    return <span className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>—</span>;
  }

  return (
    <div className="break-analysis-metric-cell flex flex-col gap-1.5 min-w-[7rem]">
      <span
        className={`break-analysis-metric-value block text-[11px] leading-snug font-semibold font-mono whitespace-normal break-words ${
          isText ? (isDark ? "text-slate-200" : "text-gray-800") : theme.text
        }`}
      >
        {display}
      </span>
      {showBar && bar}
    </div>
  );
};

const DayColumnHeader = ({ day, theme, isDark, labelClass, textClass }) => (
  <div className={`break-analysis-day-col-header rounded-lg border p-2.5 min-w-[8.75rem] bg-gradient-to-b to-transparent ${isDark ? `border-slate-700/80 ${theme.header} to-slate-900/40` : `border-gray-200 ${theme.header} to-white`}`}>
    <p className={`text-xs font-bold font-mono leading-tight ${textClass}`}>{formatReportDate(day.date)}</p>
    <p className={`text-[10px] capitalize mt-0.5 leading-tight ${labelClass}`}>{day.day_name}</p>
    {!day.has_activity ? (
      <Badge className={`mt-1 text-[9px] h-4 ${isDark ? "bg-slate-700 text-slate-400" : "bg-gray-200 text-gray-600"}`}>Aktivite yok</Badge>
    ) : (
      <Badge variant="outline" className={`mt-1 text-[9px] h-4 ${isDark ? theme.soft : theme.soft}`}>
        {day.total_calls} çağrı
      </Badge>
    )}
  </div>
);

const METRIC_ROWS = [
  { id: "calls", label: "Çağrı", get: (d) => Number(d.total_calls) || 0, fmt: (v) => v, maxOf: (d) => Number(d.total_calls) || 0 },
  { id: "talk", label: "Konuşma", get: (d) => getDayTotalTalk(d), fmt: (v) => formatTalkTime(v), maxOf: (d) => getDayTotalTalk(d) },
  { id: "break", label: "Mola", get: (d) => Number(d.between_break_seconds) || 0, fmt: (v) => formatTalkTime(v), maxOf: (d) => Number(d.between_break_seconds) || 0 },
  { id: "breakCount", label: "Mola adedi", get: (d) => Number(d.break_count) || 0, fmt: (v) => v, maxOf: (d) => Number(d.break_count) || 0 },
  { id: "short", label: "<1 dk", get: (d) => Number(d.short_calls) || 0, fmt: (v) => v, maxOf: (d) => Number(d.short_calls) || 0 },
  { id: "missed", label: "Takipsiz", get: (d) => Number(d.unfollowed_missed) || 0, fmt: (v) => v, maxOf: (d) => Number(d.unfollowed_missed) || 0 },
  { id: "first", label: "İlk giden", get: (d) => d.first_call_time || "-", fmt: (v) => v, maxOf: () => 0, isText: true },
  { id: "last", label: "Son görüşme", get: (d) => d.last_call_time || "-", fmt: (v) => v, maxOf: () => 0, isText: true },
];

function DayComparisonTable({ days, isDark, labelClass, textClass, compact = false, includeActivityRow = false }) {
  const sorted = useMemo(() => sortDaysChronological(days), [days]);
  const maxByMetric = useMemo(() => {
    const map = {};
    METRIC_ROWS.forEach((row) => {
      if (row.isText) return;
      map[row.id] = Math.max(1, ...sorted.map((d) => row.maxOf(d)));
    });
    return map;
  }, [sorted]);

  if (sorted.length === 0) return null;

  return (
    <div className={`break-analysis-day-table overflow-x-auto overscroll-x-contain ${compact ? "" : "rounded-xl border " + (isDark ? "border-slate-800" : "border-gray-200")}`}>
      <table className={`w-max min-w-full text-xs ${compact ? "" : ""}`}>
        <thead>
          <tr>
            <th className={`break-analysis-day-table-label sticky left-0 z-10 text-left align-bottom p-2 min-w-[5.75rem] ${isDark ? "bg-slate-900/95" : "bg-white/95"} ${labelClass}`}>
              {compact ? "Gün" : "Metrik"}
            </th>
            {sorted.map((day, idx) => {
              const theme = DAY_PALETTE[idx % DAY_PALETTE.length];
              return (
                <th key={day.date} className="align-bottom p-1.5 min-w-[8.75rem]">
                  <DayColumnHeader day={day} theme={theme} isDark={isDark} labelClass={labelClass} textClass={textClass} />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {METRIC_ROWS.map((row) => (
            <tr key={row.id} className={isDark ? "border-t border-slate-800/80" : "border-t border-gray-100"}>
              <td className={`break-analysis-day-table-label sticky left-0 z-10 p-2 font-medium whitespace-nowrap ${isDark ? "bg-slate-900/95 text-slate-400" : "bg-white/95 text-gray-600"}`}>
                {row.label}
              </td>
              {sorted.map((day, idx) => {
                const theme = DAY_PALETTE[idx % DAY_PALETTE.length];
                const raw = row.get(day);
                const display = row.isText ? raw : row.fmt(raw);
                return (
                  <td key={`${day.date}-${row.id}`} className="p-2 align-top min-w-[8.75rem]">
                    <MetricValueCell
                      display={display}
                      isText={row.isText}
                      theme={theme}
                      isDark={isDark}
                      hasActivity={day.has_activity || row.isText}
                      showBar={!row.isText && day.has_activity && !compact}
                      bar={<MetricBar value={raw} max={maxByMetric[row.id]} barClass={theme.bar} isDark={isDark} />}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
          {includeActivityRow && (
            <tr className={isDark ? "border-t border-slate-700" : "border-t border-gray-200"}>
              <td className={`break-analysis-day-table-label sticky left-0 z-10 p-2 align-top font-medium ${isDark ? "bg-slate-900/95 text-amber-400" : "bg-white/95 text-amber-800"}`}>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Aktivite Özeti
                </span>
              </td>
              {sorted.map((day, idx) => {
                const theme = DAY_PALETTE[idx % DAY_PALETTE.length];
                return (
                  <td key={`${day.date}-activity`} className="p-2 align-top">
                    <div className={`rounded-lg border p-2 text-[10px] leading-relaxed whitespace-pre-line max-h-28 overflow-y-auto ${isDark ? `border-slate-700/80 bg-slate-800/40 ${theme.text}` : `border-gray-200 bg-gray-50/80 text-gray-700`}`}>
                      {day.activity_summary || "—"}
                    </div>
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DayComparisonCharts({ days, isDark, labelClass, textClass, forceExpanded = false }) {
  const [expanded, setExpanded] = useState(false);
  const isOpen = forceExpanded || expanded;
  const sorted = useMemo(() => sortDaysChronological(days), [days]);
  const maxCalls = Math.max(1, ...sorted.map((d) => Number(d.total_calls) || 0));
  const maxTalk = Math.max(1, ...sorted.map((d) => getDayTotalTalk(d)));
  const maxBreak = Math.max(1, ...sorted.map((d) => Number(d.between_break_seconds) || 0));

  const charts = [
    { id: "calls", label: "Çağrı adedi", icon: Phone, get: (d) => Number(d.total_calls) || 0, max: maxCalls },
    { id: "talk", label: "Toplam konuşma", icon: Clock, get: (d) => getDayTotalTalk(d), max: maxTalk, format: formatTalkTime },
    { id: "break", label: "Mola süresi", icon: BarChart3, get: (d) => Number(d.between_break_seconds) || 0, max: maxBreak, format: formatTalkTime },
  ];

  return (
    <div className={`rounded-xl border overflow-hidden ${isDark ? "border-slate-800 bg-slate-900/30" : "border-gray-200 bg-white"}`}>
      <button
        type="button"
        onClick={() => !forceExpanded && setExpanded((open) => !open)}
        disabled={forceExpanded}
        className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${isDark ? "hover:bg-slate-800/60" : "hover:bg-gray-50"}`}
      >
        <div className="flex items-start gap-2 min-w-0">
          <div className={`mt-0.5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <p className={`text-xs font-semibold flex items-center gap-1.5 ${textClass || (isDark ? "text-slate-200" : "text-gray-900")}`}>
              <BarChart3 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              Günlük grafik karşılaştırması
            </p>
            <p className={`text-[10px] mt-0.5 ${labelClass}`}>
              Çağrı adedi · Toplam konuşma · Mola süresi
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={`shrink-0 text-[10px] h-5 ${isDark ? "border-slate-600 text-slate-400" : "border-gray-300 text-gray-600"}`}
        >
          {sorted.length} gün
        </Badge>
      </button>

      {isOpen && (
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 border-t ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          {charts.map((chart) => (
            <div key={chart.id} className={`rounded-xl border p-3 ${isDark ? "border-slate-800 bg-slate-900/50" : "border-gray-200 bg-gray-50/80"}`}>
              <p className={`text-xs font-semibold mb-3 flex items-center gap-1.5 ${isDark ? "text-slate-300" : "text-gray-800"}`}>
                <chart.icon className="w-3.5 h-3.5 text-amber-500" />
                {chart.label}
              </p>
              <div className="space-y-3">
                {sorted.map((day, idx) => {
                  const theme = DAY_PALETTE[idx % DAY_PALETTE.length];
                  const val = chart.get(day);
                  const pct = chart.max > 0 ? Math.min(100, Math.round((val / chart.max) * 100)) : 0;
                  const valueLabel = chart.format ? chart.format(val) : val;
                  return (
                    <div key={`${chart.id}-${day.date}`} className="break-analysis-chart-row space-y-1.5">
                      <div className="flex items-start justify-between gap-3">
                        <span className={`text-[10px] leading-tight shrink-0 ${labelClass}`}>{formatReportDate(day.date)}</span>
                        <span className={`text-[10px] font-mono font-semibold leading-snug text-right break-words ${theme.text}`}>
                          {valueLabel}
                        </span>
                      </div>
                      <div className={`h-2.5 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                        <div className={`h-full rounded-full ${theme.bar}`} style={{ width: `${day.has_activity ? pct : 0}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DayActivitySummaryCard({ day, theme, isDark, labelClass, textClass }) {
  const notes = Array.isArray(day.notes) ? day.notes.filter(Boolean) : [];

  return (
    <article
      className={`break-analysis-activity-card shrink-0 w-[min(100%,13.5rem)] sm:w-[13.5rem] rounded-lg border-l-4 p-3 text-xs leading-relaxed ${
        isDark
          ? "border-amber-500 bg-gradient-to-br from-slate-800/90 to-slate-900/70 text-slate-200 shadow-md shadow-black/20 ring-1 ring-amber-500/15"
          : "border-amber-400 bg-gradient-to-br from-amber-50/90 to-white text-gray-800 shadow-sm ring-1 ring-amber-200/60"
      }`}
    >
      <div className={`mb-2 pb-2 border-b ${isDark ? "border-slate-700/80" : "border-amber-200/70"}`}>
        <p className={`font-mono font-bold text-sm ${textClass || (isDark ? "text-slate-100" : "text-gray-900")}`}>
          {formatReportDate(day.date)}
        </p>
        <p className={`text-[10px] capitalize mt-0.5 ${labelClass}`}>{day.day_name}</p>
        {day.has_activity ? (
          <Badge variant="outline" className={`mt-1.5 text-[9px] h-4 ${isDark ? theme.soft : theme.soft}`}>
            {day.total_calls} çağrı · {formatTalkTime(day.between_break_seconds || 0)} mola
          </Badge>
        ) : (
          <Badge className={`mt-1.5 text-[9px] h-4 ${isDark ? "bg-slate-700 text-slate-400" : "bg-gray-200 text-gray-600"}`}>
            Aktivite yok
          </Badge>
        )}
      </div>

      <p className={`font-semibold mb-1.5 flex items-center gap-1.5 text-xs ${isDark ? "text-amber-400" : "text-amber-800"}`}>
        <FileText className="w-3.5 h-3.5 shrink-0" />
        Aktivite Özeti
      </p>
      <p className={`whitespace-pre-line text-xs leading-relaxed ${isDark ? "text-slate-200" : "text-gray-800"}`}>
        {day.activity_summary || "—"}
      </p>

      {notes.length > 0 && (
        <div className={`mt-3 pt-3 border-t ${isDark ? "border-slate-700/80" : "border-amber-200/60"}`}>
          <p className={`font-semibold mb-1.5 flex items-center gap-1.5 text-xs ${isDark ? "text-slate-300" : "text-gray-700"}`}>
            <BarChart3 className="w-3.5 h-3.5 shrink-0 text-amber-500" />
            Özet Analiz
          </p>
          <ul className={`space-y-1.5 text-xs leading-relaxed ${isDark ? "text-slate-300" : "text-gray-700"}`}>
            {notes.map((note, idx) => (
              <li key={idx} className="flex gap-2">
                <span className={`shrink-0 font-bold ${isDark ? "text-amber-400" : "text-amber-600"}`}>•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

function DayActivitySummarySection({ days, isDark, labelClass, textClass, forceExpanded = false }) {
  const [expanded, setExpanded] = useState(true);
  const isOpen = forceExpanded || expanded;
  const sorted = useMemo(() => sortDaysChronological(days), [days]);

  return (
    <div className={`rounded-xl border overflow-hidden ${isDark ? "border-slate-800 bg-slate-900/30" : "border-gray-200 bg-white"}`}>
      <button
        type="button"
        onClick={() => !forceExpanded && setExpanded((open) => !open)}
        disabled={forceExpanded}
        className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${isDark ? "hover:bg-slate-800/60" : "hover:bg-gray-50"}`}
      >
        <div className="flex items-start gap-2 min-w-0">
          <div className={`mt-0.5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <p className={`text-xs font-semibold flex items-center gap-1.5 ${textClass || (isDark ? "text-slate-200" : "text-gray-900")}`}>
              <FileText className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              Aktivite Özeti & Özet Analiz
            </p>
            <p className={`text-[10px] mt-0.5 ${labelClass}`}>
              Günlük aktivite akışı ve mola analizi notları
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={`shrink-0 text-[10px] h-5 ${isDark ? "border-slate-600 text-slate-400" : "border-gray-300 text-gray-600"}`}
        >
          {sorted.length} gün
        </Badge>
      </button>

      {isOpen && (
        <div className={`p-3 sm:p-4 border-t ${isDark ? "border-slate-800 bg-slate-950/20" : "border-gray-100 bg-amber-50/20"}`}>
          <div className={`flex gap-3 pb-1 ${forceExpanded ? "flex-wrap" : "overflow-x-auto overscroll-x-contain snap-x snap-mandatory"}`}>
            {sorted.map((day, idx) => {
              const theme = DAY_PALETTE[idx % DAY_PALETTE.length];
              return (
                <div key={day.date} className="snap-start">
                  <DayActivitySummaryCard
                    day={day}
                    theme={theme}
                    isDark={isDark}
                    labelClass={labelClass}
                    textClass={textClass}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function BreakAnalysisDayColumnsPreview({ days, isDark, labelClass, textClass, onOpenFull }) {
  const sorted = sortDaysChronological(days);
  if (sorted.length < 2) return null;

  return (
    <div className={`shrink-0 px-3 sm:px-4 py-3 border-b ${isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-100 bg-gradient-to-r from-amber-50/40 to-white"}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <p className={`text-xs font-semibold flex items-center gap-1.5 ${textClass}`}>
            <Calendar className="w-3.5 h-3.5 text-amber-500" />
            Günlük Karşılaştırma
            <span className={`font-normal ${labelClass}`}>({sorted.length} gün)</span>
          </p>
          <p className={`text-[10px] mt-0.5 ${labelClass}`}>Aktivite özeti ve metrikler gün sütunlarında yan yana</p>
        </div>
        {onOpenFull && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onOpenFull}
            className={`h-7 text-[10px] shrink-0 ${isDark ? "border-amber-500/30 text-amber-300 hover:bg-amber-500/10" : "border-amber-200 text-amber-800 hover:bg-amber-50"}`}
          >
            <Maximize2 className="w-3 h-3 mr-1" />
            Tam ekran analiz
          </Button>
        )}
      </div>
      <DayComparisonTable days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} compact includeActivityRow />
    </div>
  );
}

/** Mola raporu — Özel Dönem Analizi (modal gövdesi ile aynı, sayfa içi) */
export function BreakAnalysisRangeOverviewPanel({
  agentName,
  period,
  days,
  summary,
  loading = false,
  isDark,
  textClass,
  labelClass,
}) {
  const sorted = useMemo(() => sortDaysChronological(days), [days]);

  return (
    <div className={`rounded-xl border overflow-hidden ${isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-200 bg-white"}`}>
      <div className={`px-4 py-3 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-amber-950/50 to-slate-900" : "border-gray-200 bg-gradient-to-r from-amber-50 to-white"}`}>
        <h3 className={`text-base sm:text-lg font-semibold truncate ${textClass}`}>
          <BarChart3 className="w-5 h-5 inline mr-2 text-amber-500 align-text-bottom" />
          {agentName || "Agent"}
          <span className={`font-normal ${labelClass}`}> · Özel Dönem Analizi</span>
        </h3>
        <p className={`text-[11px] sm:text-xs mt-0.5 ${labelClass}`}>
          {formatReportDate(period?.start)} – {formatReportDate(period?.end)}
          {!loading && sorted.length > 0 && (
            <>
              <span className="mx-1.5">·</span>
              {sorted.length} gün
              {summary?.active_days != null && (
                <span className="ml-1.5">· {summary.active_days} aktif gün</span>
              )}
            </>
          )}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[16rem]">
          <div className="text-center">
            <div className="spinner w-8 h-8 mx-auto mb-3" />
            <p className={`text-sm ${labelClass}`}>Özel analiz hesaplanıyor...</p>
          </div>
        </div>
      ) : sorted.length === 0 ? (
        <div className={`flex flex-col items-center justify-center min-h-[16rem] ${labelClass}`}>
          <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm">Bu dönemde analiz edilecek günlük veri bulunamadı</p>
        </div>
      ) : (
        <div className="p-3 sm:p-4 space-y-4">
          <DayComparisonCharts days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} />
          <div>
            <p className={`text-xs font-semibold mb-2 flex items-center gap-1.5 ${textClass}`}>
              <FileText className="w-3.5 h-3.5 text-amber-500" />
              Günlük metrik tablosu
            </p>
            <DayComparisonTable days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} />
          </div>
          <DayActivitySummarySection days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} />
          <BreakShiftTimelineChart days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} />
        </div>
      )}
    </div>
  );
}

export function BreakAnalysisRangeOverviewModal({
  open,
  onClose,
  agentName,
  period,
  days,
  summary,
  loading = false,
  isDark,
  textClass,
  labelClass,
}) {
  const exportRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [exportKind, setExportKind] = useState(null);
  const sorted = useMemo(() => sortDaysChronological(days), [days]);
  const forceExpanded = exporting;

  const runExport = async (kind) => {
    if (!exportRef.current || loading || sorted.length === 0) return;
    setExportKind(kind);
    setExporting(true);
    try {
      const fileName = buildBreakOverviewFileName(agentName, period?.start, period?.end, kind);
      const opts = { isDark, fileName };
      if (kind === "png") {
        await exportBreakOverviewPng(exportRef.current, opts);
        toast.success("PNG indirildi");
      } else {
        await exportBreakOverviewPdf(exportRef.current, opts);
        toast.success("PDF indirildi");
      }
    } catch (error) {
      console.error("Break overview export error:", error);
      toast.error("Dışa aktarma başarısız oldu");
    } finally {
      setExporting(false);
      setExportKind(null);
    }
  };

  if (!open) return null;

  return (
    <PanelModal open onClose={onClose} align="start">
      <PanelModalContent
        className={`panel-modal-content panel-modal-content--break-overview w-full flex flex-col rounded-xl shadow-2xl overflow-hidden ${exporting ? "break-overview-exporting" : ""} ${isDark ? "bg-slate-900 border border-slate-800" : "bg-white border border-gray-200"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={exportRef}
          className={`flex-1 min-h-0 flex flex-col break-overview-export-root ${exporting ? "break-overview-exporting" : ""}`}
        >
        <div className={`shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b ${isDark ? "border-slate-800 bg-gradient-to-r from-amber-950/50 to-slate-900" : "border-gray-200 bg-gradient-to-r from-amber-50 to-white"}`}>
          <div className="min-w-0">
            <h3 className={`text-base sm:text-lg font-semibold truncate ${textClass}`}>
              <BarChart3 className="w-5 h-5 inline mr-2 text-amber-500 align-text-bottom" />
              {agentName}
              <span className={`font-normal ${labelClass}`}> · Özel Dönem Analizi</span>
            </h3>
            <p className={`text-[11px] sm:text-xs mt-0.5 ${labelClass}`}>
              {formatReportDate(period?.start)} – {formatReportDate(period?.end)}
              {!loading && sorted.length > 0 && (
                <>
                  <span className="mx-1.5">·</span>
                  {sorted.length} gün
                  {summary?.active_days != null && (
                    <span className="ml-1.5">· {summary.active_days} aktif gün</span>
                  )}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0" data-export-hide="true">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || sorted.length === 0 || exporting}
              onClick={() => runExport("png")}
              className={`h-8 text-[10px] sm:text-xs px-2 ${isDark ? "border-slate-600 text-slate-200 hover:bg-slate-800" : "border-gray-300"}`}
              title="PNG olarak indir"
            >
              {exporting && exportKind === "png" ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <FileImage className="w-3.5 h-3.5 mr-1" />
              )}
              PNG
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || sorted.length === 0 || exporting}
              onClick={() => runExport("pdf")}
              className={`h-8 text-[10px] sm:text-xs px-2 ${isDark ? "border-slate-600 text-slate-200 hover:bg-slate-800" : "border-gray-300"}`}
              title="PDF olarak indir"
            >
              {exporting && exportKind === "pdf" ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <FileDown className="w-3.5 h-3.5 mr-1" />
              )}
              PDF
            </Button>
            <PanelModalHeaderActions showExpand={false} onClose={onClose} isDark={isDark} />
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center min-h-[16rem]">
            <div className="text-center">
              <div className="spinner w-8 h-8 mx-auto mb-3" />
              <p className={`text-sm ${labelClass}`}>Özel analiz hesaplanıyor...</p>
            </div>
          </div>
        ) : sorted.length === 0 ? (
          <div className={`flex-1 flex flex-col items-center justify-center min-h-[16rem] ${labelClass}`}>
            <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Bu dönemde analiz edilecek günlük veri bulunamadı</p>
          </div>
        ) : (
          <div className={`flex-1 min-h-0 overscroll-contain p-3 sm:p-4 space-y-4 ${exporting ? "overflow-visible" : "overflow-y-auto"}`}>
            <DayComparisonCharts days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} forceExpanded={forceExpanded} />

            <div>
              <p className={`text-xs font-semibold mb-2 flex items-center gap-1.5 ${textClass}`}>
                <FileText className="w-3.5 h-3.5 text-amber-500" />
                Günlük metrik tablosu
              </p>
              <DayComparisonTable days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} />
            </div>

            <DayActivitySummarySection days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} forceExpanded={forceExpanded} />

            <BreakShiftTimelineChart days={days} isDark={isDark} labelClass={labelClass} textClass={textClass} forceExpanded={forceExpanded} />
          </div>
        )}
        </div>
      </PanelModalContent>
    </PanelModal>
  );
}

export default BreakAnalysisRangeOverviewModal;
